import React, { useEffect } from 'react';
import styles from './styles.module.scss';
import moment from 'moment';
import GlobalConfig from '../../../../config';

// manifests

// assets
import { faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import ErrorService from '../../../../services/general/error';

// props
import { IPaginationResourceShort } from '../../../../props/general';
import { FilterOptionProps } from '../../../uiframeworks/filters/panel';

// components
import { ActionButton, CommandBar, IColumn, ICommandBarItemProps, IContextualMenuItem, Link, PrimaryButton, SearchBox, SelectionMode, ShimmeredDetailsList, Stack } from '@fluentui/react';
import { useStore } from '../../../../stores/root';
import Text from './../../../typography/text';
import SelectedFilter from '../../../uiframeworks/filters/selected';
import Pagination from '../../../uiframeworks/pagination';
import PermissionsService from '../../../../services/permissions';
import { IVesselResourceShortProps } from '../../../../props/vessels/vessel';
import GeneralService from '../../../../services/general';
import VesselService from '../../../../services/vessels/vessel';
import VesselForm from '../form';
import { NavLink } from 'react-router-dom';

type VesselsListProps = {
    qs?: string[];
    vessels?: IVesselResourceShortProps[];
    columns?: TVesselColumn[];
    hideSearch?: boolean;
    hideCommandBar?: boolean;
    hideCreateButton?: boolean;
    hideQuickFilter?: boolean;
    hideFilter?: boolean;
    variant?: 'card' | 'plain';
}

export type TVesselColumn = 'name' | 'subsidiary' | 'type' | 'flag' | 'year' | 'size';

const VesselsList: React.FC<VesselsListProps> = (props: VesselsListProps) => {
    const shownColumns: TVesselColumn[] = props.columns || [
        'name',
        'subsidiary',
        'type',
        'flag',
        'year',
        'size'
    ];

    const { banner, user } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [showingMore, setShowingMore] = React.useState<boolean>(false);
    const [vessels, setVessels] = React.useState<IVesselResourceShortProps[]>(props.vessels || []);
    const [activeVessel, setActiveVessel] = React.useState<IVesselResourceShortProps | undefined>();
    const [pagination, setPagination] = React.useState<IPaginationResourceShort | undefined>();
    const [selectedFilter, setSelectedFilter] = React.useState<FilterOptionProps[]>([]);
    const [keyword, setKeyword] = React.useState<string>("");
    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();

    const columns: IColumn[] = [
        {
            key: "name",
            name: "Name",
            fieldName: "name",
            minWidth: 250,
            maxWidth: 250,
            isMultiline: true,
            onRender: (item: IVesselResourceShortProps) => {
                return <Stack styles={{ root: { padding: '2px 0px' } }}>
                    <NavLink to={`/vessels/${item.id}`}><Text size={'small'}>{item.name}</Text></NavLink>
                </Stack>
            }
        },
        {
            key: "actions",
            name: "",
            minWidth: 30,
            maxWidth: 30,
            onRender: (item: IVesselResourceShortProps) => {
                const menuItems:IContextualMenuItem[] = [];

                return <Stack.Item styles={{ root: { padding: '0px' } }}>
                    {
                        menuItems.length > 0 ? <Stack.Item className={"detailsListActionRow"}>
                            <ActionButton className={'detailsListActionButton'} menuProps={{
                                items: menuItems
                            }} />
                        </Stack.Item> : null
                    }
                </Stack.Item >;
            }
        },
        {
            key: "subsidiary",
            name: "Subsidiary",
            minWidth: 200,
            maxWidth: 200,
            onRender: (item: IVesselResourceShortProps) => {
                return <Stack styles={{ root: { padding: '2px 0px' } }}>
                    <Text size={'small'}>{item.subsidiary.name}</Text>
                </Stack>
            }
        },
        {
            key: "type",
            name: "Type",
            minWidth: 200,
            maxWidth: 200,
            onRender: (item: IVesselResourceShortProps) => {
                return <Stack styles={{ root: { padding: '2px 0px' } }}>
                    <Text size={'small'}>{item.type.name}</Text>
                </Stack>
            }
        },
        {
            key: "year",
            name: "Year",
            minWidth: 75,
            maxWidth: 75,
            onRender: (item: IVesselResourceShortProps) => {
                return <Stack horizontal tokens={{ childrenGap: 10 }} styles={{ root: { padding: '2px 0px' } }}>
                    <Text size={'small'}>{moment(item.yearMade).format("YYYY")}</Text>
                </Stack>
            }
        },
        {
            key: "size",
            name: "Size Details",
            minWidth: 200,
            isMultiline: true,
            onRender: (item: IVesselResourceShortProps) => {
                return <Stack styles={{ root: { padding: '2px 0px' } }}>
                    <Text size={'small'}>Gross: {GeneralService.getNumberWithSeparator(item.grossTonage)}</Text>
                    <Text size={'small'}>Nett: {GeneralService.getNumberWithSeparator(item.nettTonage)}</Text>
                </Stack>
            }
        }
    ];

    useEffect(() => {
        if (!props.vessels) {
            _onRetrieveVessels();
        } else {
            setLoaded(true);
        }
    }, [keyword, window.location.search]);

    const getQueryString = () => {
        let qs: string[] = JSON.parse(JSON.stringify(props.qs || []));
        const params = (new URLSearchParams(window.location.search));

        // get existing query string
        const _p = params.get("_p");

        qs.push(`top=${GlobalConfig.defaultTop}`);
        if ((_p || "").trim() !== "") { qs.push(`page=${_p}`) }
        if (keyword && keyword.trim() !== "") { qs.push(`search=${keyword}`) }

        qs = qs.filter(function (item, pos) {
            return qs.indexOf(item) == pos;
        });

        return qs.join("&");
    }

    const _onRetrieveVessels = async () => {
        try {
            setLoaded(false);
            const qs = getQueryString();
            const results = await VesselService.retrieve(qs);

            setVessels(results.data);
            setPagination(results.pagination);
            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'retrieve_vessels_list_error',
                text: 'Failed to retrieve vessels list. Error: ' + ErrorService.getMessage(e),
                icon: faXmarkCircle,
                variant: 'error'
            });
        }
    }

    const _onKeywordChanged = (value?: string) => {
        setLoaded(true);
        setKeyword(value || "");
    }

    const getCommandBarItems = () => {
        let items: ICommandBarItemProps[] = [];
        let farItems: ICommandBarItemProps[] = [];

        if (!props.hideSearch) {
            items.push({
                key: "search",
                onRender: () => {
                    return <SearchBox placeholder={"Search ..."} onSearch={_onKeywordChanged} styles={{ root: { marginRight: 10 } }} />
                }
            });
        }

        if (!props.hideCreateButton && PermissionsService.hasPermission(['vessels.create'], user.permissions)) {
            farItems.push({
                key: "register",
                text: "Create vessel",
                iconProps: { iconName: "Add" },
                onRender: () => {
                    return <PrimaryButton text={"Create vessel"}
                        iconProps={{ iconName: "Add" }}
                        styles={{ root: { marginLeft: 10 } }}
                        onClick={() => setActiveSurface('vessel.create')} />;
                }
            });
        }

        return { items, farItems };
    }

    const _onSurfaceDismissed = (refresh?: boolean, status?: string) => {
        setActiveSurface(undefined);
        setActiveVessel(undefined);

        if (refresh) { 
            _onRetrieveVessels();
        }
    }

    return <Stack className={styles.container} tokens={{ childrenGap: 20 }}>
        {!props.hideCommandBar ? <CommandBar
            items={getCommandBarItems().items}
            farItems={getCommandBarItems().farItems}
            styles={{
                root: {
                    padding: 0,
                    height: 'unset',
                    backgroundColor: 'transparent'
                }
            }}
            ariaLabel="Use left and right arrow keys to navigate between commands" /> : null}
        {selectedFilter.length > 0 ? <SelectedFilter selected={selectedFilter} onChange={(selected) => {
            setSelectedFilter(selected);
        }} /> : null}
        <Stack tokens={{ childrenGap: 10 }}>
            <Stack className={styles[props.variant || 'card']} tokens={{ childrenGap: 20 }}>
                {pagination && vessels.length > 0 ? <Pagination currentPage={pagination.currentPage} totalPage={pagination.lastPage} text={pagination.total + " vessels found"} /> : null}
                {
                    !loaded || (loaded && vessels.length > 0) ? (
                        <>
                            <ShimmeredDetailsList
                                setKey="items"
                                items={vessels}
                                columns={columns.filter((col) => shownColumns.indexOf(col.key as TVesselColumn) > -1)}
                                selectionMode={SelectionMode.none}
                                enableShimmer={!loaded}
                                onShouldVirtualize={() => false}
                                ariaLabelForShimmer="Content is being fetched"
                                ariaLabelForGrid="Item details" />
                        </>
                    ) : null
                }
                {loaded && vessels.length < 1 ? <Stack styles={{ root: { padding: 10 } }}>
                    <Text size={'small'}>Vessels not found</Text>
                </Stack> : null}
            </Stack>
            {pagination && vessels.length > 0 ? <Pagination currentPage={pagination.currentPage} totalPage={pagination.lastPage} /> : null}
        </Stack>
        {activeSurface === 'vessel.create' ? <VesselForm onDismissed={_onSurfaceDismissed} /> : null}
        {/*activeSurface === 'leave.update' && activeLeave ? <EmployeeLeaveForm leaveId={activeLeave.id} onDismissed={_onSurfaceDismissed} /> : null}*/}
    </Stack>;
};

export default VesselsList;
